import { Button, ImageIcon, styled, SyncIcon } from 'canoe';
import React, { ChangeEvent, useState } from 'react';

import { ValueBox, ValueGroup, ValueInput, ValueToggle } from '@candu-ai/craft/ui';
import { CollectionLockedSetting } from '@candu-ai/editor-nodes/collection-settings';
import { useToggle } from 'commons/hooks';
import { MediaLibrary } from 'craft/components/MediaLibrary';
import { useCollectionsFromContext } from 'craft/components/SelectedToolbar/Collections/hooks/useCollectionsFromContext';
import { VariableHelp, VariableSummary } from 'craft/components/ToolboxVariable';
import { Section, ValidationErrorMessages } from 'craft/containers/Toolbox/components';
import { useMultiNodes } from 'craft/hooks';
import { CollectionTargetLocation, ContentCollection } from 'craft/types';

const StyledButton = styled(Button)`
  height: 32px;
`;

interface Props {
  nodeIds: string[];
}

export const ImageToolbox = ({ nodeIds }: Props) => {
  const collectionIds = useCollectionsFromContext(nodeIds);
  const [isModalOpen, toggleModal] = useToggle(false);
  const {
    props,
    actions: { setProps },
    collection,
  } = useMultiNodes(nodeIds, (node) => ({
    collection: (node?.data?.custom.collections as ContentCollection[] | undefined)?.find(
      (s) => s.targetLocation === CollectionTargetLocation.Image,
    ),
  }));

  const [activeTab, setActiveTab] = useState<string>(() => {
    if (props.src.value && !props.src.value.toLowerCase().includes('media.candulabs.com')) {
      return 'url';
    }
    return 'upload';
  });

  const imageName = props.src.value
    ? props.src.value.match(/\/?([\w-]+\..{3})(?=\?|$)/)?.[1] || null
    : null;

  return (
    <Section name="Media" data-cy="toolbox-section-media">
      {collection ? (
        <CollectionLockedSetting
          collectionId={collectionIds[0]}
          targetLocation={CollectionTargetLocation.Image}
        />
      ) : (
        <>
          <ValueGroup>
            <ValueBox responsiveToggle>
              <ValueToggle
                toggleGroup="media-upload"
                label="Upload"
                isActive={activeTab === 'upload'}
                onClick={() => setActiveTab('upload')}
              />
              <ValueToggle
                toggleGroup="media-upload"
                label="Insert from URL"
                isActive={activeTab === 'url'}
                onClick={() => setActiveTab('url')}
              />
            </ValueBox>
          </ValueGroup>
          {activeTab === 'upload' && (
            <>
              <ValueGroup>
                {imageName || props.src.value ? (
                  <>
                    <StyledButton
                      fullWidth
                      appearance="outline"
                      variant="neutral"
                      size="xsmall"
                      icon={SyncIcon}
                      onClick={toggleModal}
                    >
                      Replace Image
                    </StyledButton>
                    <ValueGroup mt="xs">
                      <ValueBox label="Image Name">
                        <ValueInput isReadOnly value={imageName || props.src.value || ''} />
                      </ValueBox>
                    </ValueGroup>
                  </>
                ) : (
                  <StyledButton
                    fullWidth
                    appearance="outline"
                    variant="neutral"
                    size="xsmall"
                    icon={ImageIcon}
                    onClick={toggleModal}
                  >
                    Choose Image
                  </StyledButton>
                )}
                <MediaLibrary
                  nodeIds={nodeIds}
                  name="src"
                  isModalOpen={isModalOpen}
                  toggleModal={toggleModal}
                />
              </ValueGroup>
            </>
          )}
          {activeTab === 'url' && (
            <>
              <ValueGroup>
                <ValueBox label="Image URL">
                  <ValueInput
                    value={props.src.value || ''}
                    placeholder="https://"
                    onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
                      setProps({
                        src: value || undefined,
                        width: props.width.value || 'auto',
                        height: !props.width.value ? props.height.value || 'auto' : 'auto',
                      });
                    }}
                  />
                </ValueBox>
              </ValueGroup>
              {props.src.value ? <VariableSummary url={props.src.value} /> : null}
              <VariableHelp />
            </>
          )}
          {(imageName || props.src.value) && (
            <ValueGroup>
              <ValueBox label="Alt Text">
                <ValueInput
                  value={props.alt.value || ''}
                  onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
                    setProps({ alt: value || undefined })
                  }
                />
              </ValueBox>
            </ValueGroup>
          )}
          <ValidationErrorMessages nodeIds={nodeIds} />
        </>
      )}
    </Section>
  );
};
